import { useMemo } from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Combobox } from 'shadcn/components/ui/Combobox';
import { Label } from 'shadcn/components/ui/Label';

/**
 * @param {Object} item
 * @returns {string}
 */
export function getSpotlightProductSku(item) {
    return item?.externalSku || item?.external_sku || '';
}

/**
 * Product picker limited to 1–2 products.
 *
 * @param {Object} props
 * @param {boolean} props.disabled
 * @param {string} props.id
 * @param {Array} props.items
 * @param {Function} props.onItemsChange - Receives the next items array
 * @param {Array} props.sellableProducts
 */
export default function SpotlightProductPicker({ disabled, id, items = [], onItemsChange, sellableProducts = [] }) {
    const selectedItems = items ?? [];
    const canAddProduct = selectedItems.length < 2;

    const options = useMemo(() => {
        const selectedSkus = new Set(selectedItems.map(getSpotlightProductSku));

        return sellableProducts
            .filter((product) => !selectedSkus.has(product.externalSku))
            .map((product) => ({
                value: product.externalSku,
                label: `${product.externalSku}: ${product.name}`,
            }));
    }, [selectedItems, sellableProducts]);

    const handleSelect = (externalSku) => {
        if (!canAddProduct) return;

        const product = sellableProducts.find((item) => item.externalSku === externalSku);
        if (!product) return;
        if (selectedItems.some((item) => getSpotlightProductSku(item) === externalSku)) return;

        onItemsChange([...selectedItems, product]);
    };

    const handleRemove = (externalSku) => {
        onItemsChange(selectedItems.filter((item) => getSpotlightProductSku(item) !== externalSku));
    };

    return (
        <div className="flex flex-col gap-2">
            <Label>Products</Label>
            <p className="text-xs text-muted-foreground">Select 1–2 products.</p>
            {selectedItems.length > 0 && (
                <ul className="flex flex-col gap-2">
                    {selectedItems.map((item) => {
                        const sku = getSpotlightProductSku(item);

                        return (
                            <li key={sku || item.id} className="flex items-center justify-between gap-2">
                                <span className="text-sm">
                                    {sku}: {item.name}
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleRemove(sku)}
                                    disabled={disabled}
                                >
                                    Remove {item.name}
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {canAddProduct && (
                <>
                    <Label htmlFor={id}>Add product</Label>
                    <Combobox
                        handleSelect={handleSelect}
                        hideCheckmark
                        id={id}
                        options={options}
                        value=""
                        disabled={disabled}
                        placeholder="Select a product"
                        commandProps={{
                            filter: (value, search) => value.toLowerCase().includes(search.toLowerCase()),
                        }}
                    />
                </>
            )}
        </div>
    );
}
